// How the values of a detail view read. Pure functions, shared by the
// detail components so that the same value reads the same everywhere.

import { formatCode } from "../Explore/productFields";

// A fixed code of the export as words: "in_service" reads "in service"
export const codeWords = (code: string | null | undefined): string =>
  formatCode(code ?? null);

// The same, with a capital first letter: "procured_under" reads
// "Procured under"
export const codeLabel = (code: string | null | undefined): string => {
  const words = codeWords(code);
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A number as written: no trailing zeros, no rounding
export const formatNumber = (value: number): string => String(value);

// A stated number of units: the number, and the document's wording where it
// says more than the number does. "6 (six)" stays "6"; "1" worded "the
// first vessel" reads "1 (the first vessel)".
export const formatQuantity = (
  quantity: number | null,
  wording: string | null,
): string => {
  if (quantity === null) {
    return wording ?? "";
  }
  const number = formatNumber(quantity);
  return wording && /\s/.test(wording.trim())
    ? `${number} (${wording})`
    : number;
};

// Significant digits a figure keeps in its fraction; its whole part is kept
// whole
const SIGNIFICANT_DIGITS = 4;

// A measured figure as the page writes it: grouped by thousands, its
// fraction cut to four significant digits ("3.505", "199.6", "15,240",
// "0.55"), so a converted value does not trail a dozen decimals
export const formatFigure = (value: number): string => {
  const magnitude = Math.abs(value);
  if (magnitude < 1) {
    return value.toLocaleString("en-US", {
      maximumSignificantDigits: SIGNIFICANT_DIGITS,
    });
  }
  const whole = Math.floor(Math.log10(magnitude)) + 1;
  return value.toLocaleString("en-US", {
    maximumFractionDigits: Math.max(0, SIGNIFICANT_DIGITS - whole),
  });
};

// The value of a specification statement: its text, one number, or a range
// with a spaced en dash between its bounds ("5 – 10"). Each number is
// divided by *divisor* first, to read in the unit picked for its row
// (`displayUnit`).
export const formatSpecificationValue = (
  statement: {
    value_min: number | null;
    value_max: number | null;
    value_text: string | null;
  },
  divisor = 1,
): string => {
  const figure = (value: number) => formatFigure(value / divisor);
  const { value_min: minimum, value_max: maximum } = statement;
  if (minimum !== null && maximum !== null) {
    // Equal once rounded: one figure
    const [low, high] = [figure(minimum), figure(maximum)];
    return low === high ? low : `${low} – ${high}`;
  }
  if (minimum !== null) {
    return `≥ ${figure(minimum)}`;
  }
  if (maximum !== null) {
    return `≤ ${figure(maximum)}`;
  }
  return statement.value_text ?? "";
};

// The first and the latest of a list of dates (YYYY-MM-DD); null when no
// entry has a date
export const dateRange = (
  dates: (string | null | undefined)[],
): { first: string; latest: string } | null => {
  const dated = dates.filter((date): date is string => Boolean(date)).sort();
  return dated.length > 0
    ? { first: dated[0], latest: dated[dated.length - 1] }
    : null;
};
